import {
  ContentPagedResponse,
  ContentPageListParams,
  ContentPageView,
  ContentPageWriteData,
  PAGE_TYPES,
  PageType,
} from "@/types/content";
import { adminUrl } from "../../utils/url";
import { HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import { IContentPagesService } from "./IContentPagesService";
import { adaptContentPage, adaptPagedContent } from "./adapters";
import { isAbortError } from "../errors/isAbortError";
import { CONTENT_PAGE_SIZE, buildContentQuery } from "./query";

/**
 * The editorial pages of the client app (about us, FAQs, the policies).
 *
 * A page is a heading plus an ordered list of items, and the API rejects one
 * with an empty `items` array — so an update always sends the whole list, never
 * a patch of it.
 */
export class ContentPagesService implements IContentPagesService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: adminUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async list(
    params: ContentPageListParams = {},
  ): Promise<ContentPagedResponse<ContentPageView>> {
    const query = buildContentQuery(params, { pageType: params.pageType });

    try {
      const response = await this.httpClient.get<unknown>(
        `/content/pages?${query.toString()}`,
        params.signal ? { signal: params.signal } : {},
      );

      return adaptPagedContent(
        response,
        adaptContentPage,
        params.size ?? CONTENT_PAGE_SIZE,
      );
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar as páginas. Atualize a página e tente novamente.",
        ),
      );
    }
  }

  async getById(id: number, signal?: AbortSignal): Promise<ContentPageView> {
    try {
      const response = await this.httpClient.get<unknown>(
        `/content/pages/${id}`,
        signal ? { signal } : {},
      );

      const page = adaptContentPage(response);
      if (!page) {
        throw new Error(
          "Não foi possível ler a página devolvida pelo servidor.",
        );
      }

      return page;
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar a página. Atualize e tente novamente.",
        ),
      );
    }
  }

  async create(data: ContentPageWriteData): Promise<number> {
    try {
      return await this.httpClient.post<number>(
        `/content/pages`,
        data,
        {},
        true,
      );
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível criar a página. Confirme o tipo de página e os itens.",
        ),
      );
    }
  }

  async update(id: number, data: ContentPageWriteData): Promise<void> {
    try {
      await this.httpClient.put<void>(`/content/pages/${id}`, data);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível atualizar a página. Tente guardar novamente.",
        ),
      );
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/content/pages/${id}`);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível eliminar a página. Tente novamente.",
        ),
      );
    }
  }

  /**
   * The accepted page types, read from `/api/admin/config/pages`.
   *
   * Falls back to the compiled-in list rather than failing: this only fills a
   * dropdown, and an empty dropdown would block editing altogether over a call
   * that is not essential to the form.
   */
  async getPageTypes(signal?: AbortSignal): Promise<PageType[]> {
    try {
      const response = await this.httpClient.get<unknown>(
        `/config/pages`,
        signal ? { signal } : {},
      );

      if (!Array.isArray(response)) return [...PAGE_TYPES];

      const known = response.filter(
        (value): value is PageType =>
          typeof value === "string" &&
          (PAGE_TYPES as readonly string[]).includes(value),
      );

      return known.length > 0 ? known : [...PAGE_TYPES];
    } catch (error) {
      if (isAbortError(error)) throw error;

      return [...PAGE_TYPES];
    }
  }
}
